import type {
  TicketVerificationCheck,
  TicketVerificationView,
  VerificationDisplayStatus,
} from '@nightshift/ui-types';

/**
 * How the evidence drawer draws the verification block.
 *
 * Pure, and separate from the component, so the decisions that matter can be asserted without
 * a renderer: which status reads as good news, when the Retry verification button appears, and
 * what a disabled one says. The five display states themselves are decided in core, from
 * canonical CI facts; nothing here may invent or upgrade one.
 */

/** Tone classes the drawer already uses elsewhere, mapped to the five display states. */
const VERIFICATION_STATUS_TONE: Record<VerificationDisplayStatus, string> = {
  Passed: 'ok',
  Failed: 'bad',
  Blocked: 'warn',
  Running: 'live',
  Pending: 'idle',
};

export function verificationStatusTone(status: VerificationDisplayStatus): string {
  return VERIFICATION_STATUS_TONE[status] ?? 'idle';
}

/** The eight characters shown, and the full SHA that goes in the title attribute. */
export function verificationHead(view: TicketVerificationView): { short: string; full: string } {
  const full = view.headSha.trim();
  return { short: full.length > 0 ? full.slice(0, 8) : 'unknown', full };
}

/**
 * One line above the rows: how many of the required checks are proven.
 *
 * Counts `Passed` only. "2 checks" with a blocked one among them is the reading that let
 * CELL-20 look finished, so the numerator is passes and nothing else.
 */
export function verificationSummary(view: TicketVerificationView): string {
  const passed = view.checks.filter((row) => row.status === 'Passed').length;
  const total = view.checks.length;
  const retries = view.retries > 0 ? ` · ${view.retries} retry attempt${view.retries === 1 ? '' : 's'} at this head` : '';
  return `${passed} of ${total} required check${total === 1 ? '' : 's'} passed${retries}`;
}

export interface VerificationRetryOffer {
  /** Whether the button is drawn at all. */
  visible: boolean;
  /** Whether pressing it would do anything. */
  enabled: boolean;
  /** Why it is disabled, in core's own wording. Null when it is enabled. */
  explanation: string | null;
}

/**
 * Whether to show "Retry verification", and what a disabled one explains.
 *
 * Shown beside a blocked result, which is the outcome a repaired environment answers. Drawn
 * disabled rather than hidden when core refuses it: "retry cannot fix this, the job never
 * existed" is the sentence an operator needs, and a missing button says nothing at all.
 */
export function verificationRetryOffer(view: TicketVerificationView): VerificationRetryOffer {
  const blocked = view.checks.some((row) => row.status === 'Blocked');
  if (!blocked && !view.retry.offered) {
    return { visible: false, enabled: false, explanation: null };
  }
  return {
    visible: true,
    enabled: view.retry.offered,
    explanation: view.retry.offered ? null : view.retry.reason,
  };
}

/**
 * Rows in the order the operator should read them: what is stopping the ticket, first.
 *
 * Within a status the contract's own order is kept, so `launch` and `capture` do not swap
 * places between two renders of the same unchanged ticket.
 */
const STATUS_ORDER: VerificationDisplayStatus[] = ['Failed', 'Blocked', 'Running', 'Pending', 'Passed'];

export function verificationRows(view: TicketVerificationView): TicketVerificationCheck[] {
  return [...view.checks].sort(
    (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
  );
}

/**
 * The producer plan a project has declared, for the settings page's diagnostics.
 *
 * Reads the stored project configuration the settings view already fetches; it is a
 * read-out of existing configuration, not a second place to edit it.
 */
export interface VerificationProducerRow {
  name: string;
  job: string;
  provides: string[];
  result: string;
}

export function producerRowsOf(config: unknown): VerificationProducerRow[] {
  if (config === null || typeof config !== 'object') return [];
  const verification = (config as Record<string, unknown>).verification;
  if (verification === null || typeof verification !== 'object') return [];
  const producers = (verification as Record<string, unknown>).producers;
  if (producers === null || typeof producers !== 'object' || Array.isArray(producers)) return [];
  const rows: VerificationProducerRow[] = [];
  for (const name of Object.keys(producers as Record<string, unknown>).sort()) {
    const raw = (producers as Record<string, unknown>)[name];
    if (raw === null || typeof raw !== 'object') continue;
    const producer = raw as Record<string, unknown>;
    const job = typeof producer.job === 'string' ? producer.job : '';
    const result = typeof producer.result === 'string' ? producer.result : '';
    const provides = Array.isArray(producer.provides)
      ? producer.provides.filter((value): value is string => typeof value === 'string')
      : [];
    if (job.length === 0 || result.length === 0 || provides.length === 0) continue;
    rows.push({ name, job, provides: [...provides].sort(), result });
  }
  return rows;
}
